import React, { useState, useEffect, useCallback } from 'react';

const PagoCuotaModal = ({
  show,
  onClose,
  selectedCuota,
  selectedPrestamo,
  formData,
  errors,
  handleInputChange,
  handleConfirmAction,
  formatDate
}) => {
  const [localErrors, setLocalErrors] = useState({});

  const toNumber = useCallback((val) => {
    if (typeof val === "number") return val;
    if (typeof val === "string") {
      let cleanVal = val.trim().replace(/S\/\s*/g, "").replace(/\s/g, "");

      if (cleanVal.includes(',') && !cleanVal.includes('.')) {
        cleanVal = cleanVal.replace(',', '.');
      }

      if (cleanVal.includes(',') && cleanVal.includes('.')) {
        cleanVal = cleanVal.replace(/,/g, '');
      }

      const num = parseFloat(cleanVal);
      return isNaN(num) ? 0 : num;
    }
    return 0;
  }, []);

  const computeMontoAPagar = useCallback(() => {
    const saldo = parseFloat(selectedCuota.saldo_pendiente ?? NaN);
    if (!Number.isNaN(saldo)) return Math.max(0, saldo);
    const base = parseFloat(selectedCuota.monto_a_pagar ?? selectedCuota.monto ?? 0) || 0;
    const excedente = parseFloat(selectedCuota.excedente_anterior ?? 0) || 0;
    return Math.max(0, base - excedente);
  }, [selectedCuota]);

  const esUltimaCuota = selectedCuota && selectedPrestamo
    ? Number(selectedCuota.numero_cuota) >= Number(selectedPrestamo.cuotas)
    : false;

  const validateMontoPagado = useCallback((value) => {
    if (!show || !selectedCuota) return null;

    const montoPagado = Math.round(toNumber(value) * 100) / 100;

    if (!Number.isFinite(montoPagado) || montoPagado <= 0) {
      return "El monto pagado debe ser un número positivo";
    }

    const totalCuotas = Number(selectedPrestamo?.cuotas ?? 0);
    const numeroCuota = Number(selectedCuota?.numero_cuota ?? 0);
    const esUltima = totalCuotas > 0 && numeroCuota >= totalCuotas;
    if (esUltima) {
      const saldoPendiente = Math.round(computeMontoAPagar() * 100) / 100;
      if (montoPagado > saldoPendiente + 0.01) {
        return "El monto pagado no puede superar al saldo pendiente de la última cuota";
      }
    }

    return null;
  }, [show, selectedCuota, selectedPrestamo, toNumber, computeMontoAPagar]);
  const validateNumeroOperacion = useCallback((value) => {
    if (!show) return null;
    if (!value.trim()) {
      return 'El número de operación es obligatorio';
    }
    if (!/^\d+$/.test(value)) {
      return 'Solo se permiten números';
    }
    return null;
  }, [show]);

  useEffect(() => {
    if (!show) return;
    setLocalErrors((prev) => ({
      ...prev,
      monto_pagado: validateMontoPagado(formData.monto_pagado),
      numero_operacion: validateNumeroOperacion(formData.numero_operacion)
    }));
  }, [show, formData.monto_pagado, formData.numero_operacion, validateMontoPagado, validateNumeroOperacion]);

  // Handle input changes with local validation
  const handleLocalInputChange = (e) => {
    const { name, value } = e.target;
    handleInputChange(e); // Call parent handler to update formData

    if (name === 'monto_pagado') {
      const error = validateMontoPagado(value);
      setLocalErrors((prev) => ({
        ...prev,
        [name]: error
      }));
    } else if (name === 'numero_operacion') {
      const error = validateNumeroOperacion(value);
      setLocalErrors((prev) => ({
        ...prev,
        [name]: error
      }));
    }
  };

  // Check if form is valid
  const isFormValid = !localErrors.monto_pagado && !localErrors.numero_operacion;

  if (!show) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-panel modal-panel-sm p-6">
        <h3 className="modal-title mb-4">
          Registrar Pago de Cuota #{selectedCuota.numero_cuota}
        </h3>

        <div className="mb-4">
          <p className="modal-text mb-1">
            Préstamo: #{selectedPrestamo.idPrestamo} - {selectedPrestamo.modalidad}
          </p>
          <p className="modal-text mb-1">
            Monto: S/ {(parseFloat(selectedCuota.monto) + parseFloat(selectedCuota.excedente_anterior || 0)).toFixed(2)}
          </p>
          <p className="modal-text mb-1">
            Excedente anterior: S/ {parseFloat(selectedCuota.excedente_anterior || 0).toFixed(2)}
          </p>
          <p className="modal-text mb-1">
            Monto a pagar: S/ {computeMontoAPagar().toFixed(2)}
          </p>
        </div>

        <div className="mb-4">
          <label className="modal-label">
            Monto a pagar
          </label>
          <input
            type="number"
            name="monto_pagado"
            value={formData.monto_pagado}
            onChange={handleLocalInputChange}
            className={`modal-input ${localErrors.monto_pagado || errors.monto_pagado ? 'border-red-500' : ''}`}
            step="0.01"
            min="0.01"
            max="999999"
            required
          />
          {(localErrors.monto_pagado || errors.monto_pagado) && (
            <p className="text-red-500 text-xs mt-1">{localErrors.monto_pagado || errors.monto_pagado}</p>
          )}
          {esUltimaCuota && (
            <p className="text-gray-500 text-xs mt-1">
              El monto pagado no puede superar al saldo pendiente de la última cuota
            </p>
          )}
        </div>

        <div className="mb-4">
          <label className="modal-label">
            Número de Operación
          </label>
          <input
            type="text"
            name="numero_operacion"
            value={formData.numero_operacion}
            onChange={handleLocalInputChange}
            className={`modal-input ${localErrors.numero_operacion || errors.numero_operacion ? 'border-red-500' : ''}`}
            maxLength="50"
            required
            pattern="\d*"
            title="Solo se permiten números"
          />
          {(localErrors.numero_operacion || errors.numero_operacion) && (
            <p className="text-red-500 text-xs mt-1">{localErrors.numero_operacion || errors.numero_operacion}</p>
          )}
        </div>

        <div className="mb-4">
          <label className="modal-label">
            Observaciones (opcional)
          </label>
          <textarea
            name="observaciones"
            value={formData.observaciones}
            onChange={handleLocalInputChange}
            className="modal-textarea"
            rows="2"
            maxLength="255"
          ></textarea>
        </div>

        <div className="modal-actions">
          <button
            onClick={onClose}
            className="btn-modal-neutral"
          >
            Cancelar
          </button>
          <button
            onClick={handleConfirmAction}
            className={`btn-modal-success ${!isFormValid ? 'cursor-not-allowed opacity-50' : ''}`}
            disabled={!isFormValid}
          >
            Registrar Pago
          </button>
        </div>
      </div>
    </div>
  );
};

export default PagoCuotaModal;
